import React, { useEffect, useMemo, useState } from 'react';
import { plural, useAdmin } from '../context';
import { CapId, StaffMemberView, StaffRole, StaffState } from '../types';
import { Empty, Switch } from '../ui';
import { COLOR_RE, ROLE_COLORS, grantable, sameSet } from './helpers';
import { RoleChip } from './parts';

interface RoleDraft {
  id: string | null; // null: a role not saved yet
  name: string;
  description: string;
  color: string;
  caps: CapId[];
  builtin: boolean;
}

const fromRole = (r: StaffRole): RoleDraft => ({ id: r.id, name: r.name, description: r.description, color: r.color, caps: r.caps.slice(), builtin: r.builtin });
const blank = (): RoleDraft => ({ id: null, name: '', description: '', color: ROLE_COLORS[0], caps: [], builtin: false });

const NAME_MAX = 32;
const DESC_MAX = 120;

export const RolesTab: React.FC<{ staff: StaffState; members: StaffMemberView[] }> = ({ staff, members }) => {
  const { act, confirm, onEscape } = useAdmin();
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const order = useMemo(() => grantable(staff), [staff]);
  const orderIds = useMemo(() => order.map((c) => c.id), [order]);

  const usage = useMemo(() => {
    const u: Record<string, number> = {};
    for (const m of members) if (m.roleId) u[m.roleId] = (u[m.roleId] || 0) + 1;
    return u;
  }, [members]);

  const saved = draft && draft.id ? staff.roles.find((r) => r.id === draft.id) || null : null;

  // The server confirmed a change: show what it stored
  useEffect(() => {
    setDraft((cur) => {
      if (!cur || !cur.id) return cur;
      const r = staff.roles.find((x) => x.id === cur.id);
      return r ? cur : null;
    });
  }, [staff]);

  useEffect(() => (draft ? onEscape(() => setDraft(null)) : undefined), [draft ? 'open' : '', onEscape]);

  const dirty = !!draft && (!saved || draft.name !== saved.name || draft.description !== saved.description || draft.color !== saved.color || !sameSet(draft.caps, saved.caps));
  const nameOk = !!draft && draft.name.trim().length > 0;
  const colorOk = !!draft && COLOR_RE.test(draft.color);
  const used = draft && draft.id ? usage[draft.id] || 0 : 0;

  const save = () => {
    if (!draft || !nameOk || !colorOk) return;
    act(
      'adminStaffRoleUpsert',
      { ...(draft.id ? { id: draft.id } : {}), name: draft.name.trim(), description: draft.description.trim(), color: draft.color, caps: orderIds.filter((c) => draft.caps.indexOf(c) !== -1) },
      'Enregistrement du rôle'
    );
    if (!draft.id) setDraft(null);
  };

  const duplicate = (r: StaffRole) => setDraft({ id: null, name: `${r.name} (copie)`.slice(0, NAME_MAX), description: r.description, color: r.color, caps: r.caps.slice(), builtin: false });

  const remove = () => {
    if (!draft || !draft.id || draft.builtin || used > 0) return;
    const d = draft;
    confirm({
      title: `Supprimer le rôle ${d.name} ?`,
      body: "Le rôle disparaît du registre. Aucun membre ne l'utilise.",
      confirmLabel: 'Supprimer le rôle',
      danger: true,
      onConfirm: () => {
        act('adminStaffRoleRemove', { id: d.id }, 'Suppression du rôle');
        setDraft(null);
      },
    });
  };

  const toggle = (cap: CapId, on: boolean) => {
    if (!draft) return;
    const next = draft.caps.filter((c) => c !== cap);
    if (on) next.push(cap);
    setDraft({ ...draft, caps: next });
  };

  return (
    <div className={'admin-panel__staffgrid' + (draft ? ' admin-panel__staffgrid--editor' : '')}>
      <div className="admin-panel__staffmain">
        <div className="admin-panel__toolbar">
          <p className="admin-panel__hint admin-panel__hint--inline">Un rôle regroupe des permissions. Les rôles intégrés se modifient mais ne se suppriment pas.</p>
          <button type="button" className="admin-panel__button admin-panel__button--primary" onClick={() => setDraft(blank())}>Nouveau rôle</button>
        </div>
        {staff.roles.length === 0 ? (
          <Empty title="Aucun rôle" text="Créez un rôle pour attribuer les mêmes permissions à plusieurs personnes en un clic." />
        ) : (
          <ul className="admin-panel__cards admin-panel__cards--roles">
            {staff.roles.map((r) => {
              const count = usage[r.id] || 0;
              const active = !!draft && draft.id === r.id;
              return (
                <li key={r.id}>
                  <button type="button" className={'admin-panel__rcard' + (active ? ' admin-panel__rcard--active' : '')} aria-pressed={active} onClick={() => setDraft(fromRole(r))}>
                    <span className="admin-panel__rcard-bar" style={{ background: r.color }} aria-hidden="true" />
                    <span className="admin-panel__rcard-body">
                      <span className="admin-panel__rcard-top">
                        <span className="admin-panel__rcard-name">{r.name}</span>
                        {r.builtin ? <span className="admin-panel__tag">Intégré</span> : null}
                      </span>
                      {r.description ? <span className="admin-panel__rcard-desc">{r.description}</span> : null}
                      <span className="admin-panel__rcard-meta">
                        {r.caps.length} {plural(r.caps.length, 'menu', 'menus')} - {count} {plural(count, 'membre', 'membres')}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {draft ? (
        <aside className="admin-panel__editor" aria-label={draft.id ? `Édition du rôle ${draft.name}` : 'Nouveau rôle'}>
          <div className="admin-panel__editor-head">
            <div className="admin-panel__editor-id">
              <h3 className="admin-panel__editor-title">{draft.id ? draft.name || 'Rôle' : 'Nouveau rôle'}</h3>
              <div className="admin-panel__editor-sub"><RoleChip role={{ id: draft.id || '', name: draft.name || 'Aperçu', description: '', color: colorOk ? draft.color : '#888888', caps: [], builtin: false }} /></div>
            </div>
            <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={() => setDraft(null)}>Fermer</button>
          </div>

          <div className="admin-panel__editor-scroll">
            <section className="admin-panel__esec">
              <label className="admin-panel__field-label admin-panel__field-label--first" htmlFor="ap-role-name">Nom</label>
              <input id="ap-role-name" className="admin-panel__input admin-panel__input--block" maxLength={NAME_MAX} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Par exemple : Modérateur de nuit" />
              <label className="admin-panel__field-label" htmlFor="ap-role-desc">Description</label>
              <textarea id="ap-role-desc" className="admin-panel__input admin-panel__input--block admin-panel__textarea admin-panel__textarea--short" maxLength={DESC_MAX} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="À quoi sert ce rôle" />
              <div className="admin-panel__field-label">Couleur</div>
              <div className="admin-panel__palette" role="radiogroup" aria-label="Couleur du rôle">
                {ROLE_COLORS.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={draft.color.toLowerCase() === c} aria-label={`Couleur ${c}`} className={'admin-panel__colorbtn' + (draft.color.toLowerCase() === c ? ' admin-panel__colorbtn--on' : '')} style={{ background: c }} onClick={() => setDraft({ ...draft, color: c })} />
                ))}
              </div>
            </section>

            <section className="admin-panel__esec" aria-label="Permissions du rôle">
              <h4 className="admin-panel__esec-title">Permissions ({draft.caps.length} sur {order.length})</h4>
              <ul className="admin-panel__matrix">
                {order.map((c) => {
                  const on = draft.caps.indexOf(c.id) !== -1;
                  return (
                    <li key={c.id} className={'admin-panel__matrix-row' + (on ? ' admin-panel__matrix-row--on' : '')}>
                      <div className="admin-panel__matrix-text">
                        <div className="admin-panel__matrix-label">{c.label}</div>
                        <div className="admin-panel__matrix-desc">{c.description}</div>
                      </div>
                      <Switch checked={on} label={c.label} onChange={(v) => toggle(c.id, v)} />
                    </li>
                  );
                })}
              </ul>
            </section>

            {draft.id ? (
              <section className="admin-panel__esec" aria-label="Utilisation">
                <h4 className="admin-panel__esec-title">Utilisation</h4>
                <p className="admin-panel__hint">
                  {used > 0 ? `${used} ${plural(used, 'membre porte', 'membres portent')} ce rôle : il ne peut pas être supprimé tant qu'il est utilisé.` : 'Aucun membre ne porte ce rôle.'}
                  {draft.builtin ? ' Rôle intégré : modifiable, jamais supprimable.' : ''}
                </p>
              </section>
            ) : null}
          </div>

          <div className="admin-panel__editor-foot">
            <div className="admin-panel__btnrow">
              {draft.id && saved ? <button type="button" className="admin-panel__button" onClick={() => duplicate(saved)}>Dupliquer</button> : null}
              {draft.id ? (
                <button type="button" className="admin-panel__button admin-panel__button--danger" disabled={draft.builtin || used > 0} title={draft.builtin ? 'Un rôle intégré ne se supprime pas' : used > 0 ? 'Retirez ce rôle à ses membres avant de le supprimer' : undefined} onClick={remove}>
                  Supprimer
                </button>
              ) : null}
            </div>
            <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!nameOk || !colorOk || !dirty} onClick={save}>
              {draft.id ? 'Enregistrer' : 'Créer le rôle'}
            </button>
          </div>
        </aside>
      ) : null}
    </div>
  );
};
